import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError, fileUrl } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, MapPin, Clock, CalendarBlank, Star, ChatCircle, CheckCircle, UploadSimple, FileText, Crown } from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { PassList, isPass } from "../components/PassList";
import { COUNTRY_NAMES, coordsOf } from "../lib/europe";
import { secOptions } from "../lib/securityTypes";

export default function Dashboard() {
  const { user } = useAuth();
  if (!user) return null;
  return user.role === "company" ? <CompanyDashboard /> : <GuardDashboard />;
}

/* ---------------- Company ---------------- */
function CompanyDashboard() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [sub, setSub] = useState(null);
  const load = () => api.get("/jobs/mine").then((r) => setJobs(r.data));
  useEffect(() => { load(); api.get("/subscription/me").then((r) => setSub(r.data)).catch(() => {}); }, []);

  // Finalize a job-posting payment on return from Stripe (works even without a configured webhook).
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/subscription/status/${sid}`);
        if (data.payment_status === "paid") {
          toast.success(t("job_live"));
          load(); setParams({}); return;
        }
        if (data.status === "expired" || attempts >= 6) { toast.error(t("pay_failed")); setParams({}); return; }
      } catch (e) { if (attempts >= 6) { setParams({}); return; } }
      setTimeout(poll, 2000);
    };
    poll();
  }, [params]);

  const accept = async (appId) => {
    try { await api.post(`/applications/${appId}/accept`); toast.success(t("accepted")); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 py-8">
      {sub && (
        <div data-testid="plan-banner" className="mb-6 bg-white border border-zinc-200 rounded-md p-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-[#E11D2A]/10 flex items-center justify-center"><Crown weight="fill" className="text-[#E11D2A]" size={20} /></div>
            <div>
              <div className="text-xs uppercase tracking-[0.15em] text-zinc-400">{t("current_plan")}</div>
              <div className="font-semibold">
                {sub.plan ? `${sub.plan_name} · ${t("posted_this_month")}: ${sub.posted_this_month}${sub.job_limit ? `/${sub.job_limit}` : ` (${t("unlimited")})`}` : t("no_active_plan")}
                {sub.job_credits > 0 ? ` · ${t("job_credits")}: ${sub.job_credits}` : ""}
              </div>
            </div>
          </div>
          <button data-testid="goto-billing" onClick={() => navigate("/billing")} className="border border-[#E11D2A] text-[#E11D2A] hover:bg-[#E11D2A]/5 font-semibold rounded-md px-4 py-2 text-sm transition-colors">{t("nav_billing")}</button>
        </div>
      )}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="font-head text-3xl font-bold tracking-tight">{t("my_jobs")}</h1>
        <PostJobDialog onCreated={load} sub={sub} />
      </div>
      <div className="mt-6 space-y-5">
        {jobs.length === 0 && <p className="text-zinc-500">{t("no_jobs")}</p>}
        {jobs.map((j) => (
          <div key={j.id} data-testid={`my-job-${j.id}`} className="bg-white border border-zinc-200 rounded-md p-5">
            <div className="flex justify-between items-start gap-3 flex-wrap">
              <div>
                <h3 className="font-semibold text-lg">{j.title}</h3>
                <div className="flex flex-wrap gap-4 text-sm text-zinc-600 mt-1">
                  <span className="flex items-center gap-1"><MapPin size={14} />{j.location_name}</span>
                  <span className="flex items-center gap-1"><CalendarBlank size={14} />{j.date}</span>
                  <span className="flex items-center gap-1"><Clock size={14} />{j.duration_hours}{t("hours")}</span>
                </div>
              </div>
              <div className="text-right">
                <div className="font-head font-black text-xl text-[#E11D2A] tabular-nums">€{j.pay}</div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${j.status === "open" ? "bg-green-100 text-green-700" : "bg-zinc-200 text-zinc-600"}`}>{t(j.status)}</span>
                {j.guards_needed > 1 && <div className="text-xs text-zinc-500 mt-1">{j.guards_filled || 0}/{j.guards_needed} {t("guards_slots")}</div>}
              </div>
            </div>
            <div className="mt-4 border-t border-zinc-100 pt-4">
              <div className="text-xs uppercase tracking-[0.15em] text-zinc-400 mb-2">{t("applicants")} ({j.applications?.length || 0})</div>
              {(!j.applications || j.applications.length === 0) && <p className="text-sm text-zinc-400">—</p>}
              <div className="space-y-2">
                {j.applications?.map((a) => (
                  <div key={a.id} data-testid={`applicant-${a.id}`} className="flex items-center justify-between gap-3 bg-zinc-50 rounded-md px-3 py-2">
                    <button onClick={() => navigate(`/profile/${a.guard_id}`)} className="flex items-center gap-2 text-sm font-medium">
                      {a.guard_name}
                      {a.guard?.rating > 0 && <span className="flex items-center gap-0.5 text-zinc-500 text-xs"><Star size={12} weight="fill" className="text-[#FFCC00]" />{a.guard.rating}</span>}
                    </button>
                    <div className="flex items-center gap-2">
                      <button data-testid={`chat-${a.guard_id}`} onClick={() => navigate(`/messages?job=${j.id}&user=${a.guard_id}`)} className="text-zinc-500 hover:text-[#E11D2A]"><ChatCircle size={20} /></button>
                      {a.status === "accepted" ? (
                        <span className="text-xs font-semibold text-green-700 flex items-center gap-1"><CheckCircle size={16} weight="fill" />{t("accepted")}</span>
                      ) : a.status === "rejected" ? (
                        <span className="text-xs text-zinc-400">{t("rejected")}</span>
                      ) : (
                        <button data-testid={`accept-${a.id}`} onClick={() => accept(a.id)} className="bg-[#E11D2A] text-white text-xs font-semibold rounded px-3 py-1.5 hover:bg-[#B0121F] transition-colors">{t("accept")}</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PostJobDialog({ onCreated, sub }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", description: "", country: "Nederland", city: "", date: "", start_time: "", duration_hours: 5, pay: 150, security_type: "object", guards_needed: 1, is_urgent: false });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setCountry = (e) => setF({ ...f, country: e.target.value });

  const included = sub?.plan ? sub.guards_per_job : 1; // null = unlimited (Elite)
  const guards = Math.max(1, parseInt(f.guards_needed) || 1);
  const extra = included == null ? 0 : Math.max(0, guards - included);
  const extraFee = sub?.extra_guard_fee ?? 4.99;
  const extraTotal = extra * extraFee;
  const hasSub = !!sub?.plan;
  const jobCredits = sub?.job_credits ?? 0;
  const spoedCredits = sub?.spoed_credits ?? 0;
  const willPay = (f.is_urgent ? spoedCredits < 1 : (!hasSub && jobCredits < 1)) || extra > 0;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const coords = coordsOf(f.country, f.city) || [52.3676, 4.9041];
    const [lat, lng] = coords;
    try {
      const { data } = await api.post("/jobs", {
        title: f.title, description: f.description, location_name: f.city, country: f.country, lat, lng,
        date: f.date, start_time: f.start_time, duration_hours: parseFloat(f.duration_hours),
        pay: parseFloat(f.pay), security_type: f.security_type,
        guards_needed: guards, is_urgent: f.is_urgent, origin_url: window.location.origin,
      });
      if (data.checkout_required && data.url) { window.location.href = data.url; return; }
      toast.success(t("post_job"));
      setOpen(false); onCreated();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="open-post-job" className="bg-[#E11D2A] text-white hover:bg-[#B0121F] font-semibold rounded-md px-5 py-2.5 transition-colors flex items-center gap-2"><Plus weight="bold" size={18} />{t("post_job")}</button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-head">{t("post_job")}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <L label={t("job_title")}><input data-testid="job-title" required value={f.title} onChange={set("title")} className="inp2" /></L>
          <L label={t("sec_type")}>
            <select data-testid="job-security-type" value={f.security_type} onChange={set("security_type")} className="inp2 bg-white">
              {secOptions(t).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </L>
          <div className="grid grid-cols-2 gap-3">
            <L label={t("country")}>
              <select data-testid="job-country" value={f.country} onChange={setCountry} className="inp2 bg-white">
                {COUNTRY_NAMES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </L>
            <L label={t("location")}>
              <input data-testid="job-city" required value={f.city} onChange={set("city")} placeholder={t("location_placeholder")} className="inp2" />
            </L>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <L label={t("date")}><input data-testid="job-date" type="date" required value={f.date} onChange={set("date")} className="inp2" /></L>
            <L label={t("start_time")}><input data-testid="job-time" type="time" value={f.start_time} onChange={set("start_time")} className="inp2" /></L>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <L label={t("duration")}><input data-testid="job-duration" type="number" step="0.5" required value={f.duration_hours} onChange={set("duration_hours")} className="inp2" /></L>
            <L label={t("pay")}><input data-testid="job-pay" type="number" step="1" required value={f.pay} onChange={set("pay")} className="inp2" /></L>
          </div>
          <L label={t("guards_needed_label")}>
            <input data-testid="job-guards-needed" type="number" min="1" step="1" required value={f.guards_needed} onChange={set("guards_needed")} className="inp2" />
          </L>
          {included != null && (
            extra > 0 ? (
              <div data-testid="extra-guards-cost" className="text-sm rounded-md bg-[#FFCC00]/10 border border-[#FFCC00]/40 text-[#8a6d00] px-3 py-2">
                {t("guards_extra_cost")}: {extra} × €{extraFee.toFixed(2).replace(".", ",")} = <b>€{extraTotal.toFixed(2).replace(".", ",")}</b> <span className="opacity-70">({t("excl_vat")})</span>
              </div>
            ) : (
              <p className="text-xs text-zinc-400">{included} {t("guards_incl_note")}</p>
            )
          )}
          <L label={t("description")}><textarea data-testid="job-desc" rows={3} value={f.description} onChange={set("description")} className="inp2" /></L>
          <label className="flex items-start gap-3 rounded-md border border-[#E11D2A]/40 bg-[#E11D2A]/5 px-3 py-2.5 cursor-pointer">
            <input data-testid="job-urgent" type="checkbox" checked={f.is_urgent} onChange={(e) => setF({ ...f, is_urgent: e.target.checked })} className="mt-0.5 h-4 w-4 accent-[#E11D2A]" />
            <span className="text-sm"><span className="font-semibold text-[#E11D2A]">{t("is_urgent_label")}</span><br /><span className="text-zinc-500 text-xs">{t("is_urgent_hint")}</span></span>
          </label>
          <div data-testid="post-payment-note" className="rounded-md bg-[#0F141C] border border-[#2A3340] px-3 py-2.5 text-xs text-zinc-400 leading-relaxed">
            <span className="font-semibold text-zinc-200">Belangrijk:</span> De kosten voor het plaatsen van deze opdracht worden aan GuardLink betaald. De vergoeding voor de uitgevoerde werkzaamheden betaal je na afloop rechtstreeks aan de zzp'er. GuardLink verwerkt deze betaling niet.
          </div>
          <DialogFooter>
            <button data-testid="submit-job" disabled={busy} className="bg-[#E11D2A] text-white hover:bg-[#B0121F] font-semibold rounded-md px-6 py-2.5 transition-colors disabled:opacity-60">{busy ? t("loading") : (willPay ? t("to_payment") : t("submit"))}</button>
          </DialogFooter>
        </form>
        <style>{`.inp2{width:100%;background:#0F141C;border:1px solid #2A3340;border-radius:.375rem;padding:.5rem .75rem;font-size:.875rem;color:#E7E9EC;outline:none}.inp2::placeholder{color:#6B7280}.inp2:focus{box-shadow:0 0 0 2px rgba(59,102,255,.6);border-color:#3B66FF}`}</style>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Guard ---------------- */
function GuardDashboard() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  useEffect(() => { api.get("/jobs/mine").then((r) => setApps(r.data)); }, []);

  const today = new Date().toISOString().slice(0, 10);
  const accepted = apps.filter((a) => a.status === "accepted" && a.job);
  const upcoming = accepted.filter((a) => !a.job.date || a.job.date >= today);
  const completed = accepted.filter((a) => a.job.date && a.job.date < today);

  return (
    <div className="max-w-5xl mx-auto px-5 py-8">
      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-1"><ProfileEditor /></div>
        <div className="md:col-span-2">
          <Tabs defaultValue="applications">
            <TabsList data-testid="guard-tabs" className="mb-4">
              <TabsTrigger data-testid="tab-applications" value="applications">{t("tab_applications")}</TabsTrigger>
              <TabsTrigger data-testid="tab-my-jobs" value="myjobs">{t("tab_my_jobs")} ({accepted.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="applications">
              <div className="space-y-3">
                {apps.length === 0 && <p className="text-zinc-500">{t("no_jobs")}</p>}
                {apps.map((a) => a.job && <AppCard key={a.id} a={a} t={t} navigate={navigate} />)}
              </div>
            </TabsContent>

            <TabsContent value="myjobs">
              <div data-testid="guard-jobs-folder" className="space-y-6">
                <div>
                  <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-400 mb-2">{t("upcoming_jobs")} ({upcoming.length})</h2>
                  <div className="space-y-3">
                    {upcoming.length === 0 && <p className="text-sm text-zinc-400">—</p>}
                    {upcoming.map((a) => <AppCard key={a.id} a={a} t={t} navigate={navigate} />)}
                  </div>
                </div>
                <div>
                  <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-400 mb-2">{t("completed_jobs")} ({completed.length})</h2>
                  <div className="space-y-3">
                    {completed.length === 0 && <p className="text-sm text-zinc-400">{t("no_completed_jobs")}</p>}
                    {completed.map((a) => <AppCard key={a.id} a={a} t={t} navigate={navigate} completed />)}
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}

function AppCard({ a, t, navigate, completed }) {
  return (
    <div data-testid={`application-${a.id}`} className={`bg-white border border-zinc-200 rounded-md p-4 ${completed ? "opacity-90" : ""}`}>
      <div className="flex justify-between items-start gap-3">
        <div>
          <button onClick={() => navigate(`/jobs/${a.job.id}`)} className="font-semibold text-left hover:text-[#E11D2A]">{a.job.title}</button>
          <div className="flex flex-wrap gap-3 text-sm text-zinc-600 mt-1">
            <span className="flex items-center gap-1"><MapPin size={14} />{a.job.location_name}</span>
            <span className="flex items-center gap-1"><CalendarBlank size={14} />{a.job.date}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="font-head font-black text-[#E11D2A] tabular-nums">€{a.job.pay}</div>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${a.status === "accepted" ? "bg-green-100 text-green-700" : a.status === "rejected" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`}>{t(a.status)}</span>
        </div>
      </div>
      {a.status === "accepted" && (
        <button data-testid={`msg-company-${a.id}`} onClick={() => navigate(`/messages?job=${a.job.id}&user=${a.job.company_id}`)} className="mt-3 text-sm text-[#E11D2A] font-semibold flex items-center gap-1"><ChatCircle size={16} />{t("contact")}</button>
      )}
    </div>
  );
}

function ProfileEditor() {
  const { t } = useLang();
  const { user, setUser } = useAuth();
  const [me, setMe] = useState(null);
  const [bio, setBio] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => { api.get("/auth/me").then((r) => { setMe(r.data); setBio(r.data.bio || ""); }); }, []);

  const saveBio = async () => {
    await api.put("/profile", { bio });
    toast.success(t("save"));
  };
  const upload = async (e, kind) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/upload?kind=${kind}`, fd);
      const r = await api.get("/auth/me"); setMe(r.data);
      if (kind === "avatar") setUser({ ...user, avatar_path: r.data.avatar_path });
      toast.success("OK");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  if (!me) return null;
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-5">
      <div className="flex flex-col items-center">
        <div className="w-20 h-20 rounded-full bg-zinc-100 overflow-hidden border border-zinc-200">
          {me.avatar_path ? <img src={fileUrl(me.avatar_path)} alt="avatar" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center font-head font-black text-2xl text-zinc-400">{me.name?.[0]}</div>}
        </div>
        <label className="mt-2 text-xs text-[#E11D2A] font-semibold cursor-pointer">
          <input data-testid="upload-avatar" type="file" accept="image/*" className="hidden" onChange={(e) => upload(e, "avatar")} />
          {t("nav_profile")}
        </label>
        <div className="font-semibold mt-1">{me.name}</div>
        {me.rating > 0 && <div className="flex items-center gap-1 text-sm text-zinc-500"><Star size={14} weight="fill" className="text-[#FFCC00]" />{me.rating} ({me.rating_count})</div>}
      </div>

      <div className="mt-5">
        <label className="block text-sm font-medium mb-1.5">{t("bio")}</label>
        <textarea data-testid="bio-input" rows={3} value={bio} onChange={(e) => setBio(e.target.value)} className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E11D2A]" />
        <button data-testid="save-bio" onClick={saveBio} className="mt-2 w-full bg-zinc-900 text-white text-sm font-semibold rounded-md py-2 hover:bg-zinc-700 transition-colors">{t("save")}</button>
      </div>

      {me.role === "guard" && (
        <div className="mt-5">
          <div className="text-xs uppercase tracking-[0.15em] text-zinc-400 mb-2">{t("section_passes")}</div>
          <PassList documents={me.documents} />
        </div>
      )}

      <div className="mt-5">
        <div className="text-xs uppercase tracking-[0.15em] text-zinc-400 mb-2">{t("documents")}</div>
        <div className="space-y-1.5">
          {me.documents?.filter((d) => !isPass(d)).map((d, i) => (
            <a key={i} data-testid={`doc-${i}`} href={fileUrl(d.path)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-zinc-700 hover:text-[#E11D2A]"><FileText size={16} /> <span className="truncate">{d.name}</span></a>
          ))}
        </div>
        <label className="mt-3 flex items-center justify-center gap-2 border border-dashed border-zinc-300 rounded-md py-2.5 text-sm font-medium text-zinc-600 cursor-pointer hover:border-[#E11D2A] hover:text-[#E11D2A] transition-colors">
          <input data-testid="upload-doc" type="file" className="hidden" onChange={(e) => upload(e, "document")} disabled={uploading} />
          <UploadSimple size={16} /> {uploading ? t("loading") : t("upload_doc")}
        </label>
      </div>
    </div>
  );
}

const L = ({ label, children }) => (
  <div><label className="block text-sm font-medium mb-1.5">{label}</label>{children}</div>
);
